import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AuthEntryCard } from "@/features/auth/components/AuthEntryCard";
import { RegistrationBackButton } from "@/features/auth/components/RegistrationBackButton";
import { AuthField } from "@/features/auth/components/AuthField";
import { Button } from "@/shared/components/ui/Button";
import { CheckCircleIcon } from "@/shared/components/ui/icons/CheckCircleIcon";
import { XCircleIcon } from "@/shared/components/ui/icons/XCircleIcon";
import { configService } from "@/features/auth/services/config.service";
import { registrationService } from "@/features/auth/services/registration.service";
import {
  getRegistrationState,
  resetRegistration,
  setIdentityData,
  setBiometricData,
} from "@/features/auth/store/registration.store";
import type { IdentityDocumentType } from "@/features/auth/services/registration.types";

const MAX_IDENTITY_ATTEMPTS = 3;

const DEFAULT_US_DOCUMENT_TYPES: IdentityDocumentType[] = [
  { value: 3, code: "PASSPORT", name: "Pasaporte", nameEn: "Passport" },
  { value: 4, code: "DRIVERS_LICENSE", name: "Licencia de conducir", nameEn: "Driver's License" },
];

function normalizeCedula(value: string) {
  return value.replace(/\D/g, "").slice(0, 10);
}

function normalizeDactilar(value: string) {
  return value
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase()
    .slice(0, 6);
}

function isCedulaValid(value: string): boolean {
  if (!/^\d{10}$/.test(value)) return false;
  const provincia = parseInt(value.slice(0, 2), 10);
  if (provincia < 1 || provincia > 24) return false;
  if (parseInt(value[2], 10) >= 6) return false;
  if (new Set(value).size === 1) return false;
  const coef = [2, 1, 2, 1, 2, 1, 2, 1, 2];
  const suma = coef.reduce((acc, c, i) => {
    let v = parseInt(value[i], 10) * c;
    if (v >= 10) v -= 9;
    return acc + v;
  }, 0);
  const mod = suma % 10;
  return parseInt(value[9], 10) === (mod === 0 ? 0 : 10 - mod);
}

function isDactilarValid(value: string) {
  return /^[A-Z0-9]{6}$/.test(value);
}

function isPassportValid(value: string): boolean {
  return /^[A-Z0-9]{6,12}$/.test(value);
}

function isPeruDocumentValid(value: string): boolean {
  return /^[A-Z0-9]{6,13}$/.test(value);
}

function isUsDocumentValid(value: string, documentType: number): boolean {
  return (documentType === 3 || documentType === 4) && /^\d{13}$/.test(value);
}

function normalizeUsDocument(value: string, documentType: number): string {
  if (documentType !== 3 && documentType !== 4) return "";
  return value.replace(/\D/g, "").slice(0, 13);
}

function usDocumentPlaceholder(documentType: number): string {
  return documentType === 3 || documentType === 4 ? "1234567890123" : "";
}

function isPortalAccountMessage(message?: string) {
  const normalized = message?.trim().toLowerCase() ?? "";

  return (
    normalized.includes("cuenta en el portal") ||
    normalized.includes("portal account")
  );
}

function isCountryResidenceMismatchMessage(message?: string) {
  const normalized = message?.trim().toLowerCase() ?? "";

  return (
    normalized.includes("pais seleccionado no coincide") ||
    normalized.includes("país seleccionado no coincide") ||
    normalized.includes(
      "datos ingresados no coinciden con el país seleccionado",
    ) ||
    normalized.includes(
      "datos ingresados no coinciden con el pais seleccionado",
    ) ||
    normalized.includes("selected country does not match") ||
    normalized.includes("entered data does not match the selected country")
  );
}

export function AuthIdentityVerificationPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("register");
  const isEcuador = getRegistrationState().country === "EC";
  const country = getRegistrationState().country;
  const isPeru = country === "PE";
  const isUnitedStates = country === "US";
  const [cedula, setCedula] = useState("");
  const [dactilar, setDactilar] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_IDENTITY_ATTEMPTS);
  const [showAccountExistsModal, setShowAccountExistsModal] = useState(false);
  const [showLegacyPortalModal, setShowLegacyPortalModal] = useState(false);
  const [showMaxAttemptsModal, setShowMaxAttemptsModal] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<IdentityDocumentType[]>(
    DEFAULT_US_DOCUMENT_TYPES,
  );
  const [documentType, setDocumentType] = useState<number>(3);

  useEffect(() => {
    if (!isUnitedStates) return;
    void registrationService.getDocumentTypes().then((result) => {
      if (result.success && result.data.identityTypes.length > 0) {
        setDocumentTypes(result.data.identityTypes);
      }
    });
  }, [isUnitedStates]);

  // Si hay biometricToken en el store el usuario volvió desde la pantalla de biometría.
  // El correlationId del backend ya está vinculado a la cédula anterior y no puede
  // reutilizarse con otra cédula distinta — hay que iniciar sesión nueva desde consentimiento.
  useEffect(() => {
    const { correlationId, biometricToken, biometricCompleted, country } = getRegistrationState();
    if (!correlationId) {
      navigate("/", { replace: true });
      return;
    }
    if (!country) {
      navigate("/register/country", { replace: true });
      return;
    }
    // Antes de completar biometría el token no puede reutilizarse: volver a
    // identidad debe iniciar un registro nuevo. Una biometría ya completada,
    // en cambio, sí permite regresar visualmente a este paso desde OTP.
    if (biometricToken && !biometricCompleted) {
      resetRegistration();
      navigate("/register/consent", { replace: true });
    }
  }, [navigate]);

  const documentValid = useMemo(
    () =>
      isEcuador
        ? isCedulaValid(cedula)
        : isUnitedStates
          ? isUsDocumentValid(cedula, documentType)
          : isPeru
            ? isPeruDocumentValid(cedula)
            : isPassportValid(cedula),
    [isEcuador, isPeru, isUnitedStates, documentType, cedula],
  );
  const dactilarValid = useMemo(() => isDactilarValid(dactilar), [dactilar]);
  const dactilarLengthError =
    isEcuador && dactilar.length > 0 && dactilar.length < 6
      ? t("register:identity.error.dactilarLength")
      : null;
  const canContinue = isEcuador
    ? documentValid && dactilarValid && !isLoading
    : documentValid && !isLoading;

  function handleAttemptFailed(errorCode?: string, backendMessage?: string) {
    if (
      errorCode === "ERR_ALREADY_REGISTERED" ||
      (errorCode === "ERR_REGISTRATION_INVERSIONIST" &&
        isPortalAccountMessage(backendMessage))
    ) {
      setShowAccountExistsModal(true);
      return;
    }

    if (
      errorCode === "ERR_REGISTRATION_INVERSIONIST" &&
      isCountryResidenceMismatchMessage(backendMessage)
    ) {
      setErrorMessage(
        backendMessage ?? t("register:identity.error.countryResidenceMismatch"),
      );
      return;
    }

    if (errorCode === "ERR_LEGACY_PORTAL_REQUIRED") {
      setShowLegacyPortalModal(true);
      return;
    }

    if (
      errorCode === "BIOMETRY_EXTERNAL_SERVICE_ERROR" ||
      errorCode === "NETWORK_ERROR" ||
      errorCode === "INTERNAL_ERROR"
    ) {
      setErrorMessage(t("register:identity.error.serviceUnavailable"));
      return;
    }

    const remaining = attemptsLeft - 1;
    setAttemptsLeft(remaining);

    if (remaining <= 0) {
      setShowMaxAttemptsModal(true);
      return;
    }

    const messageKey =
      errorCode === "BIOMETRY_TOKEN_ERROR"
        ? "register:identity.error.dactilarMismatch"
        : "register:identity.error.mismatch";

    setErrorMessage(t(messageKey, { count: remaining }));
  }

  async function handleNext() {
    if (!canContinue) return;

    setIsLoading(true);
    setErrorMessage(null);

    const { correlationId, countryResidenceCode } = getRegistrationState();

    if (!correlationId || !countryResidenceCode) {
      navigate("/", { replace: true });
      return;
    }

    const validateResult = await registrationService.validateInversionist(
      correlationId,
      {
        documentNumber: cedula,
        countryResidenceCode,
        documentType: isUnitedStates ? documentType : undefined,
      },
    );

    if (!validateResult.success) {
      setIsLoading(false);
      handleAttemptFailed(validateResult.errorCode, validateResult.message);
      return;
    }

    if (!validateResult.data?.isInversionist) {
      setIsLoading(false);
      setErrorMessage(t("register:identity.error.notInversionist"));
      return;
    }

    const features = await configService.getFeatures();
    const needsBiometric =
      isEcuador &&
      (!features.biometricPilotEnabled ||
        features.biometricPilotCedulas.includes(cedula));

    if (!needsBiometric) {
      const bypassResult = await registrationService.startWithoutBiometry(
        correlationId,
        cedula,
      );
      setIsLoading(false);
      if (bypassResult.success) {
        setIdentityData(cedula, dactilar);
        navigate("/register/otp", { replace: true });
      } else {
        handleAttemptFailed(bypassResult.errorCode, bypassResult.message);
      }
      return;
    }

    const tokenResult = await registrationService.generarTokenBiometria({
      cedula,
      dactilar,
    });

    setIsLoading(false);

    if (tokenResult.success) {
      setIdentityData(cedula, dactilar);
      setBiometricData(
        tokenResult.data.token,
        tokenResult.data.codigoId,
        tokenResult.data.correlationId,
        tokenResult.data.requestId,
        tokenResult.data.processId,
      );
      navigate("/register/biometric", { replace: true });
      return;
    }

    handleAttemptFailed(tokenResult.errorCode, tokenResult.message);
  }

  return (
    <>
      <AuthEntryCard
        variant="identity"
        contentClassName="text-center"
      >
        <section className="mt-10 w-full max-w-96 text-center">
          <h1 className="m-0 text-xl leading-tight font-bold text-text-primary">
            {t("register:identity.title")}
          </h1>
        </section>

        <div className="mt-8 flex w-full max-w-96 flex-col gap-5">
          {isUnitedStates && documentTypes.length > 0 ? (
            <label className="flex flex-col gap-2 text-left text-sm font-medium text-text-primary">
              <span>{t("register:identity.documentType.label")}</span>
              <select
                className="min-h-12 rounded-xl border border-[#d7e4ef] bg-white px-4 text-sm"
                onChange={(event) => {
                  const nextType = Number(event.target.value);
                  setDocumentType(nextType);
                  setCedula("");
                  setErrorMessage(null);
                }}
                value={documentType}
              >
                {documentTypes.map((type) => (
                  <option key={type.value} value={type.value}>{i18n.language.startsWith("en") ? type.nameEn : type.name}</option>
                ))}
              </select>
            </label>
          ) : null}

          <AuthField
            autoComplete="off"
            inputMode={isEcuador || isUnitedStates ? "numeric" : "text"}
            label={
              isEcuador
                ? t("register:identity.cedula.label")
                : isUnitedStates
                  ? t("register:identity.document.label")
                  : t("register:identity.passport.label")
            }
            onChange={(event) => {
              setCedula(
                isEcuador
                  ? normalizeCedula(event.target.value)
                  : isUnitedStates
                    ? normalizeUsDocument(event.target.value, documentType)
                    : event.target.value
                        .replace(/[^a-zA-Z0-9]/g, "")
                        .toUpperCase()
                        .slice(0, isPeru ? 13 : 12),
              );
              setErrorMessage(null);
            }}
            onKeyDown={
              !isEcuador
                ? (event) => {
                    if (event.key === "Enter") void handleNext();
                  }
                : undefined
            }
            maxLength={isEcuador ? 10 : isUnitedStates ? 13 : isPeru ? 13 : 12}
            placeholder={isEcuador ? "1234567890" : isUnitedStates ? usDocumentPlaceholder(documentType) : isPeru ? "12345678" : "AB123456"}
            trailingAdornment={
              documentValid ? (
                <CheckCircleIcon />
              ) : cedula.length > 0 ? (
                <XCircleIcon />
              ) : null
            }
            value={cedula}
          />

          {isEcuador ? (
            <AuthField
              autoComplete="off"
              inputMode="text"
              label={t("register:identity.dactilar.label")}
              onChange={(event) => {
                setDactilar(normalizeDactilar(event.target.value));
                setErrorMessage(null);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") void handleNext();
              }}
              placeholder="AB1234"
              trailingAdornment={dactilarValid ? <CheckCircleIcon /> : null}
              value={dactilar}
            />
          ) : null}

          {dactilarLengthError ? (
            <p className="-mt-2 text-sm leading-6 text-[#e02d3c]" role="alert">
              {dactilarLengthError}
            </p>
          ) : null}

          {errorMessage ? (
            <p className="-mt-2 text-sm leading-6 text-[#e02d3c]" role="alert">
              {errorMessage}
            </p>
          ) : null}
        </div>

        <div className="mt-8 flex w-full max-w-96 items-center justify-center gap-3">
          <RegistrationBackButton />
          <Button
            className="h-9 w-28 px-5 py-1.5 text-sm disabled:bg-[#f1f1f3] disabled:text-[#8c939b] disabled:opacity-100"
            disabled={!canContinue}
            onClick={() => void handleNext()}
          >
            {isLoading
              ? t("register:identity.btn.verifying")
              : t("register:identity.btn.next")}
          </Button>
        </div>
      </AuthEntryCard>

      {showMaxAttemptsModal ? (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute inset-0 bg-[rgba(5,20,31,0.55)]" />
          <div className="relative z-10 flex w-full max-w-sm flex-col items-center rounded-md bg-white px-6 py-8 text-center shadow-xl">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full bg-[#ffebee] text-2xl font-bold text-[#ff384d]"
              aria-hidden="true"
            >
              !
            </div>
            <h2 className="mt-4 text-base font-bold leading-5 text-text-primary">
              {t("register:identity.maxAttemptsModal.title")}
            </h2>
            <p className="mt-3 text-sm leading-6 text-text-muted">
              {t("register:identity.maxAttemptsModal.description")}
            </p>
            <div className="mt-8 w-full">
              <Button
                className="min-h-9 bg-[#ffd447] px-5 py-1.5 text-sm text-[#1f2937] hover:bg-[#f3ca3f] active:bg-[#e2bb39]"
                onClick={() => navigate("/")}
              >
                {t("register:identity.maxAttemptsModal.btn")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {showLegacyPortalModal ? (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute inset-0 bg-[rgba(5,20,31,0.55)]" />
          <div className="relative z-10 flex w-full max-w-sm flex-col items-center rounded-md bg-white px-6 py-8 text-center shadow-xl">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full bg-[#ffebee] text-2xl font-bold text-[#ff384d]"
              aria-hidden="true"
            >
              !
            </div>
            <h2 className="mt-4 text-base font-bold leading-5 text-text-primary">
              {t("register:identity.legacyModal.title")}
            </h2>
            <p className="mt-3 text-sm leading-6 text-text-muted">
              {t("register:identity.legacyModal.description")}
            </p>
            <div className="mt-8 w-full">
              <Button
                className="min-h-9 bg-[#ffd447] px-5 py-1.5 text-sm text-[#1f2937] hover:bg-[#f3ca3f] active:bg-[#e2bb39]"
                onClick={() => navigate("/")}
              >
                {t("register:identity.legacyModal.btn.close")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {showAccountExistsModal ? (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute inset-0 bg-[rgba(5,20,31,0.55)]" />
          <div className="relative z-10 flex w-full max-w-sm flex-col items-center rounded-md bg-white px-6 py-8 text-center shadow-xl">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full bg-[#ffebee] text-2xl font-bold text-[#ff384d]"
              aria-hidden="true"
            >
              !
            </div>
            <h2 className="mt-4 text-base font-bold leading-5 text-text-primary">
              {t("register:identity.modal.title")}
            </h2>
            <p className="mt-3 text-sm leading-6 text-text-muted">
              {t("register:identity.modal.description")}
            </p>
            <div className="mt-8 flex w-full flex-col gap-2">
              <Button
                className="min-h-9 bg-[#ffd447] px-5 py-1.5 text-sm text-[#1f2937] hover:bg-[#f3ca3f] active:bg-[#e2bb39]"
                onClick={() => navigate("/login")}
              >
                {t("register:identity.modal.btn.login")}
              </Button>
              <Button
                className="min-h-9 border-[#ffd447] px-5 py-1.5 text-sm text-[#1f4f82] hover:bg-[#fff6cc]"
                onClick={() => navigate("/forgot-password")}
                variant="secondary"
              >
                {t("register:identity.modal.btn.recover")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
